import { backendApi } from "./backend.api";
import {
  IStoreDocument,
  StoreDocumentStatus,
  StoreDocumentType,
} from "shared/models/models";
import { IListPagination } from "shared/utils/pagination";
import { getSpaceId } from "shared/utils/space";

export interface IStoreDocumentListQuery {
  date?: string;
  status?: StoreDocumentStatus;
  type?: StoreDocumentType;
  address?: number;
  page?: number;
  visitId?: number;
  spaceId?: number;
}

interface productsQuery {
  date?: string;
  product?: number;
  type?: StoreDocumentType;
  address?: number;
  spaceId?: number;
}

interface conductParams {
  id: number;
  status: StoreDocumentStatus;
}

export const storeDocumentApi = backendApi.injectEndpoints({
  endpoints: (build) => {
    return {
      listStoreDocument: build.query<
        IListPagination<IStoreDocument>,
        IStoreDocumentListQuery
      >({
        query: (queryParams) => {
          const spaceId = getSpaceId();
          const params: IStoreDocumentListQuery = { ...queryParams };
          if (spaceId) {
            params.spaceId = spaceId;
          }
          return {
            url: "/store",
            method: "GET",
            params: params,
          };
        },
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result?.data.map(({ id }) => ({
                  type: "StoreDocument" as const,
                  id,
                })),
                "StoreDocument",
              ]
            : ["StoreDocument"],
      }),
      listStoreDocumentProducts: build.query<IStoreDocument[], productsQuery>({
        query: (queryParams) => ({
          url: "/store/products",
          method: "GET",
          params: queryParams,
        }),
      }),
      getStoreDocument: build.query<IStoreDocument, string>({
        query: (id) => ({ url: `/store/${id}`, method: "GET" }),
        transformErrorResponse: (response: { status: string | number }) =>
          response.status,
        providesTags: (item) => [{ type: "StoreDocument", id: item?.id }],
      }),
      getStoreDocumentByOrder: build.query<IStoreDocument, string>({
        query: (id) => ({ url: `/store/find-by-order/${id}`, method: "GET" }),
        transformErrorResponse: (response: { status: string | number }) =>
          response.status,
        providesTags: (item) => [{ type: "StoreDocument", id: item?.id }],
      }),
      addStoreDocument: build.mutation<IStoreDocument, Partial<IStoreDocument>>(
        {
          query: (payload) => {
            const spaceId = getSpaceId();
            const params: Partial<IStoreDocument> = { ...payload };
            if (spaceId) {
              params.spaceId = spaceId;
            }
            return {
              url: "/store",
              method: "POST",
              data: params,
            };
          },
          invalidatesTags: [
            "StoreDocument",
            "RestProducts",
            "RestHistoryProduct",
          ],
          transformResponse: (response: IStoreDocument) => response,
        },
      ),
      updateStoreDocument: build.mutation<
        IStoreDocument,
        Partial<IStoreDocument>
      >({
        query: (payload) => ({
          url: `/store/${payload.id}`,
          method: "PATCH",
          data: payload,
        }),
        invalidatesTags: (item) => [
          { type: "StoreDocument", id: item?.id },
          "RestProducts",
          "RestHistoryProduct",
        ],
      }),
      conductStoreDocument: build.mutation<IStoreDocument, conductParams>({
        query: (payload) => ({
          url: `/store/conduct`,
          method: "PATCH",
          data: payload,
        }),
        invalidatesTags: (item) => [
          { type: "StoreDocument", id: item?.id },
          "RestProducts",
          "RestHistoryProduct",
        ],
      }),
      deleteStoreDocument: build.mutation<IStoreDocument, number>({
        query: (id) => ({
          url: `/store/${id}`,
          method: "DELETE",
          credentials: "include",
        }),
        invalidatesTags: [
          "StoreDocument",
          "RestProducts",
          "RestHistoryProduct",
        ],
      }),
    };
  },
});

export const {
  useListStoreDocumentProductsQuery,
  useListStoreDocumentQuery,
  useAddStoreDocumentMutation,
  useDeleteStoreDocumentMutation,
  useConductStoreDocumentMutation,
  useGetStoreDocumentQuery,
  useUpdateStoreDocumentMutation,
} = storeDocumentApi;
